import React, { useEffect, useState } from 'react';
import {
  Plus,
  Clock,
  Loader2,
  AlertTriangle,
  Zap,
  Globe2,
  Tv,
  Radio,
  Share2,
  Search,
  Archive,
  ChevronRight,
  BookOpen,
  Languages,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  getStories,
  getContentItemsByStory,
  archiveStory,
  getCurrentUser,
} from '../services/store';
import {
  Story,
  ContentItemWithVersion,
  User,
  LANGUAGE_LABELS,
  WORKFLOW_STATUS_LABELS,
  WORKFLOW_STATUS_COLORS,
  getUrgencyLabel,
} from '../types';
import { supabase } from '../services/supabaseClient';

interface DashboardProps {
  onEditStory: (id: string) => void;
  onNewStory: () => void;
}

interface StoryWithItems extends Story {
  content_items?: ContentItemWithVersion[];
}

const Dashboard: React.FC<DashboardProps> = ({ onEditStory, onNewStory }) => {
  const [stories, setStories] = useState<StoryWithItems[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterBreaking, setFilterBreaking] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [expandedStories, setExpandedStories] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadData();
  }, [searchQuery, filterBreaking]);

  const loadData = async () => {
    setLoading(true);
    try {
      const user = await getCurrentUser();
      setCurrentUser(user);

      const fetchedStories = await getStories({
        search: searchQuery || undefined,
        breaking_only: filterBreaking,
        limit: 50,
      });

      // Load content items for each story
      const storiesWithItems = await Promise.all(
        fetchedStories.map(async (story) => {
          const items = await getContentItemsByStory(story.id);
          return { ...story, content_items: items };
        })
      );

      setStories(storiesWithItems);
    } catch (error) {
      console.error('Error loading dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (confirm("Are you sure you want to archive this story?")) {
      await archiveStory(id);
      loadData();
    }
  };

  const toggleStoryExpand = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const newExpanded = new Set(expandedStories);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedStories(newExpanded);
  };

  const getBreakingBadge = (story: Story) => {
    if (!story.is_breaking) return null;

    const colors = {
      flash: 'bg-red-600 text-white animate-pulse',
      urgent: 'bg-orange-500 text-white',
      developing: 'bg-yellow-500 text-black',
    };

    return (
      <span className={`px-2 py-0.5 text-xs font-bold rounded ${colors[story.breaking_level || 'developing']}`}>
        <Zap className="h-3 w-3 inline mr-1" />
        {story.breaking_level?.toUpperCase() || 'BREAKING'}
      </span>
    );
  };

  const getPlatformIcons = (items: ContentItemWithVersion[]) => {
    const platforms = new Set(items.map((i) => i.platform));
    return (
      <div className="flex items-center space-x-1">
        {platforms.has('tv') && <Tv className="h-3.5 w-3.5 text-blue-500" aria-label="TV" />}
        {platforms.has('radio') && <Radio className="h-3.5 w-3.5 text-orange-500" aria-label="Radio" />}
        {platforms.has('web') && <Globe2 className="h-3.5 w-3.5 text-green-500" aria-label="Web" />}
        {platforms.has('social') && <Share2 className="h-3.5 w-3.5 text-purple-500" aria-label="Social" />}
      </div>
    );
  };

  const getStatusBadge = (status: string) => {
    const color = WORKFLOW_STATUS_COLORS[status as keyof typeof WORKFLOW_STATUS_COLORS] || '#9CA3AF';
    const label = WORKFLOW_STATUS_LABELS[status as keyof typeof WORKFLOW_STATUS_LABELS] || status;

    return (
      <span
        className="px-2 py-0.5 text-xs font-medium rounded-full"
        style={{ backgroundColor: `${color}20`, color }}
      >
        {label}
      </span>
    );
  };

  if (loading && stories.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-7xl mx-auto h-full overflow-y-auto animate-fade-in">
      {/* Header */}
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900 dark:text-white">Stories</h1>
          <p className="text-zinc-500 dark:text-zinc-400 mt-1">
            Manage your newsroom stories and content
          </p>
        </div>
        <Button
          onClick={onNewStory}
          className="flex items-center"
        >
          <Plus className="h-4 w-4 mr-2" /> New Story
        </Button>
      </div>


      {/* Search & Filters */}
      <div className="flex items-center space-x-4 mb-8">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400 z-10" />
          <Input
            type="text"
            placeholder="Search stories by slug..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Button
          onClick={() => setFilterBreaking(!filterBreaking)}
          variant={filterBreaking ? "destructive" : "outline"}
          className="flex items-center"
        >
          <AlertTriangle className="h-4 w-4 mr-2" />
          Breaking Only
        </Button>
      </div>

      {/* Stories List */}
      {stories.length === 0 ? (
        <div className="text-center py-20 bg-white dark:bg-zinc-900 rounded-xl border border-dashed border-zinc-300 dark:border-zinc-700">
          <div className="bg-zinc-50 dark:bg-zinc-800 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
            <Globe2 className="h-8 w-8 text-zinc-400 dark:text-zinc-500" />
          </div>
          <h3 className="text-lg font-medium text-zinc-900 dark:text-white">No stories yet</h3>
          <p className="text-zinc-500 dark:text-zinc-400 mb-6 max-w-sm mx-auto">
            Create your first story to get started with content production.
          </p>
          <Button
            onClick={onNewStory}
            variant="link"
            className="text-blue-600 dark:text-blue-400"
          >
            Create New Story &rarr;
          </Button>
        </div>
      ) : (
        <div className="space-y-6">
          {stories.map((story, index) => (
            <div
              key={story.id}
              className="bg-white dark:bg-zinc-900 rounded-xl border border-zinc-200 dark:border-zinc-800 shadow-sm overflow-hidden animate-fade-in animate-slide-in-up"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              {/* Story Header */}
              <div
                onClick={() => onEditStory(story.id)}
                className="flex items-center p-5 hover:bg-zinc-50 dark:hover:bg-zinc-800/50 transition-colors cursor-pointer group"
              >
                {/* Urgency Indicator */}
                <div
                  className="w-1 h-12 rounded-full mr-4"
                  style={{
                    backgroundColor:
                      story.urgency <= 2 ? '#EF4444' : story.urgency <= 4 ? '#F97316' : '#10B981',
                  }}
                  title={getUrgencyLabel(story.urgency)}
                />

                {/* Story Info */}
                <div className="flex-1 min-w-0 mr-4">
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate group-hover:text-blue-600 transition-colors">
                      {story.slug}
                    </span>
                    {getBreakingBadge(story)}
                    <span className="px-1.5 py-0.5 text-[10px] font-bold bg-zinc-200 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 rounded">
                      {LANGUAGE_LABELS[story.canonical_language]}
                    </span>
                  </div>
                  <div className="flex items-center text-xs text-zinc-500 dark:text-zinc-400 space-x-3">
                    <span className="flex items-center">
                      <Clock className="h-3 w-3 mr-1" />
                      {new Date(story.created_at).toLocaleDateString()}
                    </span>
                    {story.content_items && story.content_items.length > 0 && (
                      <>
                        <span className="w-1 h-1 rounded-full bg-zinc-300 dark:bg-zinc-600" />
                        {getPlatformIcons(story.content_items)}
                        <span>{story.content_items.length} item(s)</span>
                      </>
                    )}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center space-x-2">
                  {story.content_items && story.content_items.length > 0 && (
                    <Button
                      onClick={(e) => toggleStoryExpand(story.id, e)}
                      variant="ghost"
                      size="icon"
                      className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300"
                    >
                      <ChevronRight
                        className={`h-4 w-4 transition-transform ${
                          expandedStories.has(story.id) ? 'rotate-90' : ''
                        }`}
                      />
                    </Button>
                  )}
                  <Button
                    onClick={(e) => handleArchive(story.id, e)}
                    variant="ghost"
                    size="icon"
                    className="text-zinc-400 hover:text-orange-600 opacity-0 group-hover:opacity-100"
                    title="Archive"
                  >
                    <Archive className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              {/* Expanded Content Items */}
              {expandedStories.has(story.id) && story.content_items && story.content_items.length > 0 && (
                <div className="border-t border-zinc-100 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50">
                  <div className="px-4 py-2">
                    <p className="text-xs font-semibold text-zinc-500 dark:text-zinc-400 uppercase tracking-wider mb-2">
                      Content Items
                    </p>
                    <div className="space-y-2">
                      {story.content_items.map((item) => (
                        <div
                          key={item.id}
                          onClick={() => onEditStory(story.id)}
                          className="flex items-center p-2 bg-white dark:bg-zinc-900 rounded-lg border border-zinc-200 dark:border-zinc-700 hover:border-blue-300 dark:hover:border-blue-700 cursor-pointer transition-colors"
                        >
                          <div className="flex items-center space-x-2 flex-1">
                            {item.platform === 'tv' && <Tv className="h-4 w-4 text-blue-500" />}
                            {item.platform === 'radio' && <Radio className="h-4 w-4 text-orange-500" />}
                            {item.platform === 'web' && <Globe2 className="h-4 w-4 text-green-500" />}
                            {item.platform === 'social' && <Share2 className="h-4 w-4 text-purple-500" />}
                            <span className="text-sm font-medium text-zinc-700 dark:text-zinc-300">
                              {item.current_version?.headline || `${item.platform.toUpperCase()} - ${LANGUAGE_LABELS[item.language]}`}
                            </span>
                            <span className="px-1.5 py-0.5 text-[10px] font-bold bg-zinc-100 dark:bg-zinc-700 text-zinc-500 dark:text-zinc-400 rounded">
                              {LANGUAGE_LABELS[item.language]}
                            </span>
                            {item.is_canonical && (
                              <span 
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400 rounded"
                                title="Canonical (original) content"
                              >
                                <BookOpen className="h-2.5 w-2.5" />
                                Canon
                              </span>
                            )}
                            {!item.is_canonical && item.derived_from_content_id && (
                              <span 
                                className="inline-flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 rounded"
                                title="Translation"
                              >
                                <Languages className="h-2.5 w-2.5" />
                                Trans
                              </span>
                            )}
                          </div>
                          {getStatusBadge(item.workflow_status)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Dashboard;
